import {
  PlanningEvent,
  PlanningStatus,
  PlanningSeverity,
} from "../models/planning-event.model";

export function expeditionsToPlanningEvents(bls: any[]): PlanningEvent[] {
  if (!bls?.length) return [];

  return bls
    .filter((bl) => bl.date != null)
    .map((bl) => {
      const client = bl.tiers?.denomination ?? "";
      const nbPalettes = bl.palettes?.length ?? 0;

      return {
        id: `expedition:${bl.id}`,
        source: "expedition" as const,
        kind: "delivery",
        reference: {
          // Pas de route détail BL direct : on ouvre la liste des BL
          // (detail est un dialog, pas une route dédiée dans l'app).
          label: client || `BL ${bl.numero ?? "?"}`,
          route: ["/expeditions/bls"],
        },
        datePrevu: new Date(bl.date).toISOString(),
        dateReel: new Date(bl.date).toISOString(),
        status: "completed" as PlanningStatus,
        severity: 0 as PlanningSeverity,
        quantite:
          nbPalettes > 0
            ? { prevu: nbPalettes, reel: nbPalettes, unite: "pal" }
            : undefined,
        payload: {
          client,
          numero: bl.numero ?? "?",
          etatLabel: "Expédiée",
          nbPalettes: nbPalettes > 0 ? `${nbPalettes} pal.` : "",
        },
      } satisfies PlanningEvent;
    });
}

export function chargementsToPlanningEvents(
  chargements: any[],
): PlanningEvent[] {
  if (!chargements?.length) return [];

  return chargements
    .filter((c) => c.date != null)
    .map((c) => {
      const { status, severity } = deriveChargementStatus(c.etat);
      // Accepte deux shapes : (1) Chargement complet avec `transporteur`
      // objet et `bls[]`/`palettes[]` populés (legacy `loadAll()` heavy) ;
      // (2) DTO léger du nouvel endpoint `/chargements/planning` qui
      // expose `transporteurDenomination` + compteurs scalaires `nbBls` /
      // `nbPalettes`. Permet de basculer du heavy au light sans casser
      // les autres consommateurs (widget dashboard).
      const transporteur =
        c.transporteur?.denomination ?? c.transporteurDenomination ?? "";
      const nbBls = c.nbBls ?? c.bls?.length ?? 0;
      const nbPalettes = c.nbPalettes ?? c.palettes?.length ?? 0;

      return {
        id: `chargement:${c.id}`,
        source: "expedition" as const,
        kind: "chargement",
        reference: {
          label: transporteur || `Charg. ${c.numero ?? "?"}`,
          route: ["/expeditions/en-cours"],
        },
        datePrevu: new Date(c.date).toISOString(),
        dateReel: c.etat === "CHARGE" ? new Date(c.date).toISOString() : null,
        status,
        severity,
        quantite:
          nbPalettes > 0
            ? { prevu: nbPalettes, reel: nbPalettes, unite: "pal" }
            : undefined,
        payload: {
          transporteur,
          numero: c.numero ?? "?",
          etat: c.etat,
          etatLabel: chargementEtatToLabel(c.etat),
          nbBls: nbBls > 0 ? `${nbBls} BL` : "",
          nbPalettes: nbPalettes > 0 ? `${nbPalettes} pal.` : "",
        },
      } satisfies PlanningEvent;
    });
}

function deriveChargementStatus(etat: string): {
  status: PlanningStatus;
  severity: PlanningSeverity;
} {
  switch (etat) {
    case "CHARGE":
      return { status: "completed", severity: 0 };
    case "PRET":
      return { status: "in_progress", severity: 1 };
    case "EN_PREPARATION":
    default:
      return { status: "pending", severity: 0 };
  }
}

function chargementEtatToLabel(etat: string): string {
  const labels: Record<string, string> = {
    EN_PREPARATION: "En préparation",
    PRET: "Prêt",
    CHARGE: "Chargé",
  };
  return labels[etat] ?? etat;
}
